import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Shield, FileText, Clock, AlertTriangle, CheckCircle, Filter, RefreshCw, Trash2, Eye, Download, Loader2 } from 'lucide-react';

const TABS = [
  { id: 'summary', label: 'Summary', icon: Shield },
  { id: 'consent', label: 'Consent Records', icon: FileText },
  { id: 'access', label: 'Access Events', icon: Eye },
  { id: 'audit', label: 'Audit Log', icon: Clock },
  { id: 'retention', label: 'Retention', icon: AlertTriangle },
];

const EVENT_TYPES = [
  { value: '', label: 'All Events' },
  { value: 'document_upload', label: 'Document Upload' },
  { value: 'document_view', label: 'Document View' },
  { value: 'document_download', label: 'Document Download' },
  { value: 'document_delete', label: 'Document Delete' },
  { value: 'consent_granted', label: 'Consent Granted' },
  { value: 'consent_withdrawn', label: 'Consent Withdrawn' },
  { value: 'data_access', label: 'Data Access' },
  { value: 'data_export', label: 'Data Export' },
  { value: 'retention_cleanup', label: 'Retention Cleanup' },
  { value: 'encryption_key_rotated', label: 'Key Rotation' },
  { value: 'breach_detected', label: 'Breach Detected' },
];

export default function AdminComplianceLogs() {
  const [activeTab, setActiveTab] = useState('summary');
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [consentRecords, setConsentRecords] = useState([]);
  const [accessEvents, setAccessEvents] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [retentionStatus, setRetentionStatus] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ event_type: '', start_date: '', end_date: '', case_id: '' });
  const [refreshing, setRefreshing] = useState(false);
  const [cleanupRunning, setCleanupRunning] = useState(false);

  useEffect(() => {
    loadTabData();
  }, [activeTab, page, filters]);

  const loadTabData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'summary':
          const summaryRes = await api.get('/compliance/summary');
          setSummary(summaryRes.data);
          break;
        case 'consent':
          const consentRes = await api.get('/compliance/consent-records', { params: { limit: 50, offset: (page - 1) * 50, case_id: filters.case_id || undefined } });
          setConsentRecords(consentRes.data.records);
          setTotalRecords(consentRes.data.total);
          break;
        case 'access':
          const accessRes = await api.get('/compliance/access-events', { params: { limit: 50, offset: (page - 1) * 50, case_id: filters.case_id || undefined } });
          setAccessEvents(accessRes.data.events);
          setTotalRecords(accessRes.data.total);
          break;
        case 'audit':
          const auditRes = await api.get('/compliance/logs', { params: { limit: 50, offset: (page - 1) * 50, event_type: filters.event_type || undefined, start_date: filters.start_date || undefined, end_date: filters.end_date || undefined } });
          setAuditLogs(auditRes.data.logs);
          setTotalRecords(auditRes.data.total);
          break;
        case 'retention':
          const retRes = await api.get('/compliance/retention-status');
          setRetentionStatus(retRes.data);
          break;
      }
    } catch (err) {
      console.error('Failed to load compliance data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    setPage(1);
    loadTabData();
  };

  const handleCleanup = async () => {
    setCleanupRunning(true);
    try {
      await api.post('/compliance/cleanup-expired');
      handleRefresh();
    } catch (err) {
      console.error('Cleanup failed:', err);
    } finally {
      setCleanupRunning(false);
    }
  };

  const exportData = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleString() : '—';
  const truncateHash = (hash) => hash ? `${hash.slice(0, 8)}...${hash.slice(-8)}` : '—';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-2">Compliance Log</h1>
          <p className="text-sm text-gray-500">DPDP Act 2023 / Aadhaar Act / IT Act 2000 audit trail</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-2 bg-surface border border-gray-300 rounded-sm text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleCleanup}
            disabled={cleanupRunning}
            className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-sm text-sm hover:bg-red-700 disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            {cleanupRunning ? 'Cleaning...' : 'Cleanup Expired'}
          </button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => { setActiveTab(id); setPage(1); }}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition ${
              activeTab === id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'audit' && (
        <div className="flex gap-3 mb-4 flex-wrap">
          <select
            value={filters.event_type}
            onChange={(e) => setFilters({ ...filters, event_type: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-sm text-sm"
          >
            {EVENT_TYPES.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <input
            type="date"
            value={filters.start_date}
            onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-sm text-sm"
            placeholder="Start date"
          />
          <input
            type="date"
            value={filters.end_date}
            onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-sm text-sm"
            placeholder="End date"
          />
        </div>
      )}

      {(activeTab === 'consent' || activeTab === 'access') && (
        <div className="flex gap-3 mb-4">
          <input
            type="text"
            value={filters.case_id}
            onChange={(e) => setFilters({ ...filters, case_id: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-sm text-sm w-64"
            placeholder="Filter by Case ID..."
          />
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : (
        <>
          {activeTab === 'summary' && summary && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  ['Total Documents', summary.total_documents, 'text-blue-600', FileText],
                  ['Consent Rate', `${summary.consent_rate}%`, summary.consent_rate === 100 ? 'text-green-600' : 'text-amber-600', CheckCircle],
                  ['Encryption Rate', `${summary.encryption_rate}%`, summary.encryption_rate === 100 ? 'text-green-600' : 'text-amber-600', Shield],
                  ['Access Events', summary.total_access_events, 'text-purple-600', Eye],
                ].map(([title, value, color, Icon]) => (
                  <div key={title} className="bg-surface border border-gray-200 rounded-sm p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <Icon className={`w-5 h-5 ${color}`} />
                      <span className="text-sm text-gray-500">{title}</span>
                    </div>
                    <p className={`text-2xl font-bold ${color}`}>{value}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-surface border border-gray-200 rounded-sm p-5">
                  <p className="text-sm text-gray-500 mb-1">Aadhaar Documents</p>
                  <p className="text-xl font-bold text-red-600">{summary.aadhaar_documents}</p>
                  <p className="text-xs text-gray-400 mt-1">Sensitive — Aadhaar Act s.8(2) applies</p>
                </div>
                <div className="bg-surface border border-gray-200 rounded-sm p-5">
                  <p className="text-sm text-gray-500 mb-1">PAN Documents</p>
                  <p className="text-xl font-bold text-amber-600">{summary.pan_documents}</p>
                  <p className="text-xs text-gray-400 mt-1">Sensitive — DPDP Act s.3(12)</p>
                </div>
                <div className="bg-surface border border-gray-200 rounded-sm p-5">
                  <p className="text-sm text-gray-500 mb-1">Audit Events (30d)</p>
                  <p className="text-xl font-bold text-blue-600">{summary.recent_audit_events_30d}</p>
                  <p className="text-xs text-gray-400 mt-1">Cryptographically chained</p>
                </div>
              </div>

              {summary.compliance_checks && summary.compliance_checks.length > 0 && (
                <div className="bg-surface border border-gray-200 rounded-sm p-5">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Recent Compliance Checks</h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-500">Check</th>
                        <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-500">Status</th>
                        <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-500">Checked At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.compliance_checks.map((check, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="px-3 py-2 font-medium">{check.check_name}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${check.status === 'pass' ? 'bg-green-100 text-green-800' : check.status === 'fail' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                              {check.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-500">{formatDate(check.checked_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'consent' && (
            <div className="border border-gray-200">
              <table className="w-full" data-testid="consent-table">
                <thead>
                  <tr className="border-b border-gray-200 bg-surface">
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Timestamp</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Case</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Category</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Purpose</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">IP</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Retention</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Encrypted</th>
                  </tr>
                </thead>
                <tbody>
                  {consentRecords.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400 text-sm">No consent records found</td></tr>
                  ) : consentRecords.map((r) => (
                    <tr key={r.document_id} className="border-b border-gray-100">
                      <td className="px-4 py-3 text-xs font-mono text-gray-500">{formatDate(r.consent_timestamp)}</td>
                      <td className="px-4 py-3 text-xs font-mono">{r.case_id?.slice(0, 8)}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-mono text-xs uppercase bg-surface px-1.5 py-0.5 rounded">{r.category}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{r.purpose}</td>
                      <td className="px-4 py-3 text-xs font-mono text-gray-500">{r.consent_ip || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">{formatDate(r.retention_until)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium ${r.encrypted ? 'text-green-600' : 'text-red-600'}`}>
                          {r.encrypted ? 'Yes' : 'No'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'access' && (
            <div className="border border-gray-200">
              <table className="w-full" data-testid="access-table">
                <thead>
                  <tr className="border-b border-gray-200 bg-surface">
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Timestamp</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Document</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Case</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Action</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Actor</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Role</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {accessEvents.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400 text-sm">No access events found</td></tr>
                  ) : accessEvents.map((e, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="px-4 py-3 text-xs font-mono text-gray-500">{formatDate(e.timestamp)}</td>
                      <td className="px-4 py-3 text-xs font-mono">{e.document_id?.slice(0, 8)}</td>
                      <td className="px-4 py-3 text-xs font-mono">{e.case_id?.slice(0, 8)}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          e.action === 'upload' ? 'bg-blue-100 text-blue-800' :
                          e.action === 'download' ? 'bg-green-100 text-green-800' :
                          e.action === 'view_list' ? 'bg-purple-100 text-purple-800' :
                          e.action === 'soft_delete' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {e.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs font-mono">{e.user_id?.slice(0, 8) || '—'}</td>
                      <td className="px-4 py-3 text-xs">{e.role}</td>
                      <td className="px-4 py-3 text-xs font-mono text-gray-500">{e.ip || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'audit' && (
            <div className="border border-gray-200">
              <table className="w-full" data-testid="audit-compliance-table">
                <thead>
                  <tr className="border-b border-gray-200 bg-surface">
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Timestamp</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Event Type</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Entity</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Action</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Actor</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">IP</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Hash</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400 text-sm">No audit logs found</td></tr>
                  ) : auditLogs.map((log) => (
                    <tr key={log.id} className="border-b border-gray-100">
                      <td className="px-4 py-3 text-xs font-mono text-gray-500">{formatDate(log.event_timestamp)}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-mono text-xs uppercase bg-surface px-1.5 py-0.5 rounded">{log.event_type}</span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-mono text-xs uppercase bg-surface px-1.5 py-0.5 rounded">{log.entity_type}</span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium">{log.action}</td>
                      <td className="px-4 py-3 text-xs font-mono text-gray-500">{log.actor_role || '—'}</td>
                      <td className="px-4 py-3 text-xs font-mono text-gray-500">{log.ip_address || '—'}</td>
                      <td className="px-4 py-3 text-xs font-mono text-gray-400" title={log.current_hash}>{truncateHash(log.current_hash)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'retention' && retentionStatus && (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-surface border border-gray-200 rounded-sm p-5">
                  <p className="text-sm text-gray-500 mb-1">Active Documents</p>
                  <p className="text-2xl font-bold text-green-600">{retentionStatus.active_documents}</p>
                </div>
                <div className="bg-surface border border-gray-200 rounded-sm p-5">
                  <p className="text-sm text-gray-500 mb-1">Expired (Pending Cleanup)</p>
                  <p className="text-2xl font-bold text-red-600">{retentionStatus.expired_documents}</p>
                </div>
                <div className="bg-surface border border-gray-200 rounded-sm p-5">
                  <p className="text-sm text-gray-500 mb-1">Deleted</p>
                  <p className="text-2xl font-bold text-gray-600">{retentionStatus.deleted_documents}</p>
                </div>
              </div>

              {Object.keys(retentionStatus.category_breakdown).length > 0 && (
                <div className="bg-surface border border-gray-200 rounded-sm p-5">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Category Breakdown</h3>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {Object.entries(retentionStatus.category_breakdown).map(([cat, count]) => (
                      <div key={cat} className="flex items-center justify-between p-3 bg-white border border-gray-100 rounded-sm">
                        <span className="font-mono text-xs uppercase text-gray-600">{cat}</span>
                        <span className="font-bold text-sm">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {totalRecords > 50 && activeTab !== 'summary' && activeTab !== 'retention' && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-gray-500">Showing {Math.min((page - 1) * 50 + 1, totalRecords)}-{Math.min(page * 50, totalRecords)} of {totalRecords}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page * 50 >= totalRecords}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
